import { createContext, useCallback, useContext, useMemo, useState } from "react";

/**
 * "A student is sitting an exam right now, don't let them navigate away."
 *
 * <p>Set by the exam session page for exactly as long as the sitting is live, and read by the
 * sidebar to disable every navigation control. Deliberately NOT route-based: the same route also
 * renders the camera-permission error and the finished/disqualified states, and locking the menu
 * there would strand the student on a page they cannot leave or act on.
 *
 * <p>The page already had a `beforeunload` guard for refresh/close and tab-switch disqualification;
 * in-app sidebar navigation was the one remaining way to walk out of a running exam by accident.
 */
const ExamLockContext = createContext({ examLocked: false, setExamLocked: () => {} });

export const ExamLockProvider = ({ children }) => {
    const [examLocked, setLocked] = useState(false);
    // Stable identity so the session page can call it from an effect without re-running it.
    const setExamLocked = useCallback((value) => setLocked(!!value), []);
    const value = useMemo(() => ({ examLocked, setExamLocked }), [examLocked, setExamLocked]);
    return <ExamLockContext.Provider value={value}>{children}</ExamLockContext.Provider>;
};

export const useExamLock = () => useContext(ExamLockContext);

export default ExamLockContext;
